import { Transition } from '@headlessui/react';
import { PanelLeftClose } from 'lucide-react';
import clsx from 'clsx';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppStore } from '../../store/useAppStore';
import { useAuthStore } from '../../store/useAuthStore';
import { navGroups, itemMatchesPath, type NavItem } from './navigation';

/**
 * The destinations inside the primary rail's selected group.
 *
 * Sits alongside the primary rail rather than replacing it, so the group you
 * are in stays visible and switching groups is one click, not two.
 */
export default function SecondaryRail() {
  const { activeRailGroup, setRailGroup, activePanel, setActivePanel } =
    useAppStore();
  const { hasRole, hasModule } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const group = navGroups.find((g) => g.id === activeRailGroup);
  const items = (group?.items || []).filter(
    (item) =>
      (!item.requiredRoles || item.requiredRoles.some((r) => hasRole(r as any))) &&
      (!item.requiredModule || hasModule(item.requiredModule))
  );
  const isOpen = Boolean(group) && items.length > 0;

  const handleClick = (item: NavItem) => {
    if (item.panel) {
      // Not a toggle: the rail collapses on click, so the item cannot be
      // clicked twice without reopening it first — at which point closing the
      // panel you just asked for would be surprising.
      setActivePanel(item.panel);
    } else if (activePanel) {
      // A route-only destination closes whatever panel was open.
      setActivePanel(null);
    }
    if (item.route) navigate(item.route);
    // Once you have arrived, the rail is in the way — several destinations
    // carry their own list column, so this is otherwise three columns of
    // chrome before any content. Reopen from the primary rail icon.
    setRailGroup(null);
  };

  const isActive = (item: NavItem) => {
    if (item.panel && activePanel === item.panel) return true;
    if (!activePanel) return itemMatchesPath(item, location.pathname);
    return false;
  };

  return (
    <Transition show={isOpen}>
      <div className="relative h-screen flex flex-col bg-rail-secondary dark:bg-rail-secondary-dark border-r border-black/10 dark:border-white/5 shrink-0 overflow-hidden">
        <Transition.Child
          enter="transition-all duration-150 ease-out"
          enterFrom="w-0 opacity-0"
          enterTo="w-52 opacity-100"
          leave="transition-all duration-100 ease-in"
          leaveFrom="w-52 opacity-100"
          leaveTo="w-0 opacity-0"
        >
          <div className="w-52 h-screen flex flex-col">
            {/* Group header, aligned with the rail's brand block */}
            <div className="flex items-center justify-between gap-2 px-3 h-14 border-b border-black/10 dark:border-white/5 shrink-0">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 truncate">
                {group?.label}
              </span>
              <button
                onClick={() => setRailGroup(null)}
                className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-black/5 dark:hover:bg-white/5 shrink-0"
                title="Collapse"
              >
                <PanelLeftClose size={14} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
              {items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleClick(item)}
                  className={clsx(
                    'w-full flex items-start gap-2.5 px-2 py-2 rounded-lg text-left transition-colors',
                    isActive(item)
                      ? 'bg-accent/10 text-accent'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/5'
                  )}
                >
                  <item.icon
                    size={15}
                    className={clsx(
                      'shrink-0 mt-0.5',
                      isActive(item) ? 'text-accent' : 'text-gray-400 dark:text-gray-500'
                    )}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-medium leading-tight truncate">
                      {item.label}
                    </span>
                    {item.description && (
                      <span
                        className={clsx(
                          'block text-[10px] leading-tight mt-0.5',
                          isActive(item)
                            ? 'text-accent/70'
                            : 'text-gray-500 dark:text-gray-500'
                        )}
                      >
                        {item.description}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </nav>
          </div>
        </Transition.Child>
      </div>
    </Transition>
  );
}
